/**
 * Dodge: the player's one defensive verb.
 *
 * Telegraphed enemy attacks (see `resolveNpcAttackWindupMs`) only matter if
 * the player can actually answer them. Without a dodge, a tell is just a
 * delay before unavoidable damage, and the only counterplay left is walking
 * out of range. The invulnerability window is the mechanical core: it makes
 * reading a telegraph and reacting to it a skill the game rewards.
 */

/** How far a dodge carries the character, in tile units. */
export const DODGE_DISTANCE_TILES = 4 as const;

/**
 * How long the dodge movement itself takes. The character is committed for
 * this window - it is a deliberate commitment, not a free reposition.
 *
 * The length of the roll clip (UAL1 `Roll`, 88 frames at 60fps) so it plays
 * at its natural speed; the host can't read the clip, so the two are kept in
 * step by hand. The client still fits the clip to this window, so a clip of
 * another length stays in sync, just no longer at 1x.
 */
export const DODGE_DURATION_MS = 1467 as const;

/**
 * How far into the roll the character has gone `elapsedMs` after starting
 * it, in tiles. The roll eases out (cubic): it bursts away at the start and
 * settles over the back of the clip, so most of the distance is covered
 * while the character is still diving, not spread evenly over the roll.
 */
export function resolveDodgeDistance(elapsedMs: number): number {
    const progress = Math.min(1, Math.max(0, elapsedMs / DODGE_DURATION_MS));
    return DODGE_DISTANCE_TILES * (1 - (1 - progress) ** 3);
}

/** The roll's fastest speed (its first instant, see `resolveDodgeDistance`), tiles per second. */
export const DODGE_PEAK_SPEED_TILES_PER_SEC = (3 * DODGE_DISTANCE_TILES) / (DODGE_DURATION_MS / 1000);

/**
 * How long into a dodge the character can still be hit: the first quarter of
 * the roll, while they are still going down into it. From then until the
 * roll ends they cannot be hit, so a dodge has to be started ahead of an
 * incoming attack rather than on the frame it lands.
 */
export const DODGE_VULNERABLE_LEAD_MS = Math.round(DODGE_DURATION_MS / 4);

/**
 * Minimum gap between dodge starts, so it cannot be spammed as primary
 * locomotion: the roll itself, then a beat before the next.
 */
export const DODGE_COOLDOWN_MS = DODGE_DURATION_MS + 640;
